import { apiClient } from '@/lib/api';
import type {
  IncidentListResponse,
  IncidentTimelineEntry,
  IncidentTypeConfig,
  InspectionTypeConfig,
  InspectionChecklistTemplate,
  InspectionListItem,
  InspectionScheduleListItem,
  RiskClassificationConfig,
  SafetyDashboardResponse,
  SafetyIncident,
  SafetyInspection,
  HazardRegisterEntry,
  HazardListResponse,
  CorrectiveAction,
  CorrectiveActionListResponse,
} from '@/types/safety.types';

// ── Types ──

export interface IncidentParams {
  siteId?: string;
  zoneId?: string;
  tunnelId?: string;
  workingPlaceId?: string;
  severity?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface CreateIncidentPayload {
  title: string;
  incidentDate: string;
  siteId: string;
  /** FR-SAF-002d — zone is mandatory per SRS unless a working place is sent. */
  zoneId?: string;
  tunnelId?: string;
  workingPlaceId?: string;
  teamId?: string;
  /** FR-SAF-003 — tenant-configured incident-type key. */
  incidentType: string;
  severity: string;
  description: string;
  immediateActions?: string;
  personsInvolved?: string[];
  /** FR-SAF-005 — optional incident photographs. */
  photos?: File[];
  /** Links this incident to the visitor it concerns, raised from the visitor detail page. */
  visitorAttendanceId?: string;
  /** The shift report the incident was raised from; the backend holds it to that report's site and shift. */
  shiftReportId?: string;
  /** Recorded as critical whatever severity is chosen. */
  isLostTime?: boolean;
}

/** FR-SAF-002 — editable correction fields for an existing incident. */
export interface UpdateIncidentPayload {
  title?: string;
  incidentDate?: string;
  severity?: string;
  description?: string;
  immediateActions?: string;
  personsInvolved?: string[];
  workingPlaceId?: string;
}

export interface CreateIncidentTypePayload {
  key: string;
  label: string;
  description?: string;
  isEnabled?: boolean;
  displayOrder?: number;
}

export interface UpdateIncidentTypePayload {
  label?: string;
  description?: string;
  isEnabled?: boolean;
  displayOrder?: number;
}

export interface UpdateIncidentStatusPayload {
  incidentId: string;
  newStatus: string;
  comment?: string;
  overrideJustification?: string;
}

// New config + amend types

export interface CreateInspectionTypePayload {
  key: string;
  label: string;
  description?: string;
  isEnabled?: boolean;
  displayOrder?: number;
  /** §8.8 row 4 — permit linkage fields. */
  requiresPermit?: boolean;
  linkedLicenseId?: string | null;
}
export interface UpdateInspectionTypePayload {
  label?: string;
  description?: string;
  isEnabled?: boolean;
  displayOrder?: number;
  /** §8.8 row 4 — pass null to unlink. */
  requiresPermit?: boolean;
  linkedLicenseId?: string | null;
}

export interface CreateChecklistTemplatePayload {
  inspectionTypeKey: string;
  itemSequence: number;
  itemDescription: string;
  guidance?: string;
  isRequired?: boolean;
  isActive?: boolean;
}
export interface UpdateChecklistTemplatePayload {
  itemSequence?: number;
  itemDescription?: string;
  guidance?: string;
  isRequired?: boolean;
  isActive?: boolean;
}

export interface UpdateRiskClassificationConfigPayload {
  lowMaxScore?: number;
  mediumMaxScore?: number;
  highMaxScore?: number;
}

export interface AmendInspectionPayload {
  reason: string;
  changes: Record<string, unknown>;
}

export interface CreateInspectionSchedulePayload {
  inspectionType: string;
  frequency: string;
  assignedRole: string;
  siteId: string;
  zoneId?: string;
  tunnelId?: string;
  workingPlaceId?: string;
  /** §8.8 schema-consistency — optional Team owner. */
  teamId?: string;
  nextDueDate: string;
  isActive?: boolean;
  /** §8.8 row 5 — statutory schedule flag + optional link to a tracked requirement. */
  isStatutory?: boolean;
  linkedComplianceRequirementId?: string;
}

export interface CreateInspectionPayload {
  inspectionType: string;
  siteId: string;
  zoneId?: string;
  tunnelId?: string;
  workingPlaceId?: string;
  /** §8.8 schema-consistency — Team that conducted the inspection. */
  teamId?: string;
  outcome: string;
  observations?: string;
  scheduleId?: string;
  checklistResults?: {
    itemDescription: string;
    result: string;
    remarks?: string;
    photoUrl?: string;
  }[];
}

/** FR-SAF-030 — hazard observation submission payload (no risk fields). */
export interface CreateHazardPayload {
  description: string;
  siteId: string;
  zoneId?: string;
  /** §8.8 schema-consistency — optional Tunnel + Team. */
  tunnelId?: string;
  workingPlaceId?: string;
  teamId?: string;
  category: string;
  dateObserved?: string;
  immediateControl?: string;
}

/** FR-SAF-031 — payload to triage an observed hazard. */
export interface TriageHazardPayload {
  action: 'PROMOTE' | 'CLOSE_NOT_SIGNIFICANT';
  // PROMOTE branch
  likelihood?: number;
  consequence?: number;
  existingControls?: string;
  residualRisk?: string;
  hazardOwner?: string;
  // CLOSE branch
  justification?: string;
}

export interface HazardFilters {
  siteId?: string;
  zoneId?: string;
  tunnelId?: string;
  category?: string;
  riskClassification?: string;
  status?: string;
  q?: string;
  page?: number;
  limit?: number;
}

export interface CorrectiveActionFilters {
  sourceType?: string;
  assignedTo?: string;
  priority?: string;
  status?: string;
  dueBefore?: string;
  page?: number;
  limit?: number;
}

export interface SafetyDashboardFilters {
  siteId?: string;
  startDate?: string;
  endDate?: string;
}

export interface CreateCorrectiveActionPayload {
  title: string;
  description: string;
  sourceType: string;
  sourceId: string;
  assignedTo: string;
  dueDate: string;
  priority: string;
}

// ── Service ──

export const safetyService = {
  // Incidents
  async getIncidents(params?: IncidentParams): Promise<IncidentListResponse> {
    const { data } = await apiClient.get<IncidentListResponse>('/safety/incidents', { params });
    return data;
  },

  async getIncidentById(id: string) {
    const { data } = await apiClient.get<SafetyIncident>(`/safety/incidents/${id}`);
    return data;
  },

  // FR-SAF-011 — full audit-backed timeline for one incident.
  async getIncidentTimeline(id: string): Promise<IncidentTimelineEntry[]> {
    const { data } = await apiClient.get<IncidentTimelineEntry[]>(`/safety/incidents/${id}/timeline`);
    return data;
  },

  /** §8.8 row 3 — fetch the linked environmental incident (or null). */
  async getLinkedEnvironmentalIncident(id: string) {
    const { data } = await apiClient.get<{ id: string; title: string; status: string } | null>(
      `/safety/incidents/${id}/linked-environmental-incident`,
    );
    return data;
  },

  async createIncident(payload: CreateIncidentPayload) {
    const { photos, personsInvolved, isLostTime, ...rest } = payload;
    const fd = new FormData();

    // Object.entries() doesn't preserve `| undefined` for optional properties
    // in its type signature — cast restores the accurate runtime type so
    // omitted fields (genuinely undefined) are skipped instead of sent as
    // the literal string "undefined".
    for (const [key, value] of Object.entries(rest) as [string, string | undefined][]) {
      if (value === undefined || value === '') continue;
      fd.append(key, value);
    }
    if (personsInvolved?.length) {
      fd.append('personsInvolved', JSON.stringify(personsInvolved));
    }
    if (isLostTime) {
      fd.append('isLostTime', String(isLostTime));
    }
    for (const file of photos ?? []) {
      fd.append('photos', file);
    }

    const { data } = await apiClient.post<SafetyIncident>('/safety/incidents', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  // FR-SAF-002 — edit an existing incident's descriptive fields.
  async updateIncident(id: string, payload: UpdateIncidentPayload) {
    const { data } = await apiClient.patch<SafetyIncident>(`/safety/incidents/${id}`, payload);
    return data;
  },

  // ── Incident Type Config (FR-SAF-003) ──

  async getIncidentTypes(): Promise<IncidentTypeConfig[]> {
    const { data } = await apiClient.get<IncidentTypeConfig[]>('/safety/incident-types');
    return data;
  },

  async getAllIncidentTypes(): Promise<IncidentTypeConfig[]> {
    const { data } = await apiClient.get<IncidentTypeConfig[]>('/safety/incident-types/all');
    return data;
  },

  async createIncidentType(payload: CreateIncidentTypePayload): Promise<IncidentTypeConfig> {
    const { data } = await apiClient.post<IncidentTypeConfig>('/safety/incident-types', payload);
    return data;
  },

  async updateIncidentType(id: string, payload: UpdateIncidentTypePayload): Promise<IncidentTypeConfig> {
    const { data } = await apiClient.patch<IncidentTypeConfig>(`/safety/incident-types/${id}`, payload);
    return data;
  },

  async deleteIncidentType(id: string): Promise<{ success: true }> {
    const { data } = await apiClient.delete<{ success: true }>(`/safety/incident-types/${id}`);
    return data;
  },

  async updateIncidentStatus(payload: UpdateIncidentStatusPayload) {
    // Incident id goes in the URL — the status route is `incidents/:id/status`.
    // Sending it in the body hits the generic `incidents/:id` update route
    // (id="status") whose strict DTO rejects `newStatus`/`incidentId`.
    const { incidentId, ...body } = payload;
    const { data } = await apiClient.patch<SafetyIncident>(`/safety/incidents/${incidentId}/status`, body);
    return data;
  },

  // Inspection Schedules
  async getInspectionSchedules(siteId?: string): Promise<InspectionScheduleListItem[]> {
    const { data } = await apiClient.get<InspectionScheduleListItem[]>('/safety/inspection-schedules', { params: { siteId } });
    return data;
  },

  async createInspectionSchedule(payload: CreateInspectionSchedulePayload) {
    const { data } = await apiClient.post<InspectionScheduleListItem>('/safety/inspection-schedules', payload);
    return data;
  },

  // Inspections
  async getInspections(params?: { siteId?: string; page?: number; limit?: number }): Promise<InspectionListItem[]> {
    const { data } = await apiClient.get<InspectionListItem[]>('/safety/inspections', { params });
    return data;
  },

  async createInspection(payload: CreateInspectionPayload) {
    const { data } = await apiClient.post<SafetyInspection>('/safety/inspections', payload);
    return data;
  },

  // Hazards
  async getHazards(params?: HazardFilters) {
    const { data } = await apiClient.get<HazardListResponse>('/safety/hazards', { params });
    return data;
  },

  async getHazardById(id: string) {
    const { data } = await apiClient.get<HazardRegisterEntry>(`/safety/hazards/${id}`);
    return data;
  },

  async createHazard(payload: CreateHazardPayload) {
    const { data } = await apiClient.post<HazardRegisterEntry>('/safety/hazards', payload);
    return data;
  },

  /** FR-SAF-031 — triage an OBSERVED hazard. */
  async triageHazard(id: string, payload: TriageHazardPayload) {
    const { data } = await apiClient.post<HazardRegisterEntry>(
      `/safety/hazards/${id}/triage`,
      payload,
    );
    return data;
  },

  // Corrective Actions
  async getCorrectiveActions(params?: CorrectiveActionFilters) {
    const { data } = await apiClient.get<CorrectiveActionListResponse>('/safety/corrective-actions', { params });
    return data;
  },

  async createCorrectiveAction(payload: CreateCorrectiveActionPayload) {
    const { data } = await apiClient.post<CorrectiveAction>('/safety/corrective-actions', payload);
    return data;
  },

  async updateCorrectiveActionStatus(id: string, status: string) {
    // Backend DTO expects `newStatus` (the field that survives the strict
    // whitelist + enum validation), not `status`.
    const { data } = await apiClient.patch<CorrectiveAction>(
      `/safety/corrective-actions/${id}/status`,
      { newStatus: status },
    );
    return data;
  },

  // Dashboard
  async getSafetyDashboard(params?: SafetyDashboardFilters): Promise<SafetyDashboardResponse> {
    const { data } = await apiClient.get<SafetyDashboardResponse>('/safety/dashboard', { params });
    return data;
  },

  // Inspection Type Config (FR-SAF-023)
  async getInspectionTypes(): Promise<InspectionTypeConfig[]> {
    const { data } = await apiClient.get<InspectionTypeConfig[]>('/safety/inspection-types');
    return data;
  },
  async getAllInspectionTypes(): Promise<InspectionTypeConfig[]> {
    const { data } = await apiClient.get<InspectionTypeConfig[]>('/safety/inspection-types/all');
    return data;
  },
  async createInspectionType(payload: CreateInspectionTypePayload) {
    const { data } = await apiClient.post<InspectionTypeConfig>('/safety/inspection-types', payload);
    return data;
  },
  async updateInspectionType(id: string, payload: UpdateInspectionTypePayload) {
    const { data } = await apiClient.patch<InspectionTypeConfig>(`/safety/inspection-types/${id}`, payload);
    return data;
  },
  async deleteInspectionType(id: string) {
    const { data } = await apiClient.delete<{ success: true }>(`/safety/inspection-types/${id}`);
    return data;
  },

  // Inspection Checklist Template (FR-SAF-024)
  async getChecklistTemplate(inspectionTypeKey: string): Promise<InspectionChecklistTemplate[]> {
    const { data } = await apiClient.get<InspectionChecklistTemplate[]>('/safety/inspection-checklists', {
      params: { inspectionTypeKey },
    });
    return data;
  },
  async getAllChecklistTemplates(): Promise<InspectionChecklistTemplate[]> {
    const { data } = await apiClient.get<InspectionChecklistTemplate[]>('/safety/inspection-checklists/all');
    return data;
  },
  async createChecklistTemplate(payload: CreateChecklistTemplatePayload) {
    const { data } = await apiClient.post<InspectionChecklistTemplate>('/safety/inspection-checklists', payload);
    return data;
  },
  async updateChecklistTemplate(id: string, payload: UpdateChecklistTemplatePayload) {
    const { data } = await apiClient.patch<InspectionChecklistTemplate>(`/safety/inspection-checklists/${id}`, payload);
    return data;
  },
  async deleteChecklistTemplate(id: string) {
    const { data } = await apiClient.delete<{ success: true }>(`/safety/inspection-checklists/${id}`);
    return data;
  },

  // Risk Classification Config (FR-SAF-033)
  async getRiskClassificationConfig(): Promise<RiskClassificationConfig> {
    const { data } = await apiClient.get<RiskClassificationConfig>('/safety/risk-classification');
    return data;
  },
  async updateRiskClassificationConfig(
    payload: UpdateRiskClassificationConfigPayload,
  ): Promise<RiskClassificationConfig> {
    const { data } = await apiClient.patch<RiskClassificationConfig>('/safety/risk-classification', payload);
    return data;
  },

  // Inspection detail / acknowledge / amend (FR-SAF-025/026)
  async getInspectionById(id: string) {
    const { data } = await apiClient.get<SafetyInspection>(`/safety/inspections/${id}`);
    return data;
  },
  async acknowledgeUnsatisfactoryInspection(id: string) {
    const { data } = await apiClient.patch<SafetyInspection>(
      `/safety/inspections/${id}/acknowledge-unsatisfactory`,
    );
    return data;
  },
  async amendInspection(id: string, payload: AmendInspectionPayload) {
    const { data } = await apiClient.post<SafetyInspection>(
      `/safety/inspections/${id}/amend`,
      payload,
    );
    return data;
  },

  // Multipart inspection submission (FR-SAF-023)
  async createInspectionMultipart(
    payload: CreateInspectionPayload & {
      photos?: File[];
      digitalAcknowledgement: boolean;
    },
  ) {
    const { photos, checklistResults, digitalAcknowledgement, ...rest } = payload;
    const fd = new FormData();
    // See createIncident() above — Object.entries() drops `| undefined` for
    // optional properties, but omitted fields are genuinely undefined here.
    for (const [key, value] of Object.entries(rest) as [string, string | undefined][]) {
      if (value === undefined || value === '') continue;
      fd.append(key, value);
    }
    fd.append('digitalAcknowledgement', String(digitalAcknowledgement));
    if (checklistResults?.length) {
      fd.append('checklistResults', JSON.stringify(checklistResults));
    }
    for (const file of photos ?? []) {
      fd.append('photos', file);
    }
    const { data } = await apiClient.post<SafetyInspection>('/safety/inspections', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};
